import {ADataGridFilter} from '@atiautomacao/ati-ui-library';
import {
    PowerStationCommunicationState,
    PowerStationPerformanceRow,
} from './PerformanceAnalysisReducer';

const isActiveSort = (sort: string | undefined): boolean => sort === 'asc' || sort === 'desc';

const COMMUNICATION_RANK_ASC: Record<PowerStationCommunicationState, number> = {
    NO_COMMUNICATION: 0,
    COMMUNICATING: 1,
    SYNCHRONIZING: 2,
};

const COMMUNICATION_RANK_DESC: Record<PowerStationCommunicationState, number> = {
    SYNCHRONIZING: 0,
    COMMUNICATING: 1,
    NO_COMMUNICATION: 2,
};

export function getActiveCommunicationSortDirection(
    filters: ADataGridFilter[]
): 'asc' | 'desc' | null {
    const communicationFilter = normalizePerformanceAnalysisSortFilters(filters)
        .find((filter) => filter.name === 'communicationState');
    const sort = communicationFilter?.sort;
    return sort === 'asc' || sort === 'desc' ? sort : null;
}

export function sortPowerStationPerformanceRows(
    rows: PowerStationPerformanceRow[],
    direction: 'asc' | 'desc'
): PowerStationPerformanceRow[] {
    const rankByState = direction === 'desc' ? COMMUNICATION_RANK_DESC : COMMUNICATION_RANK_ASC;
    return [...rows].sort((first, second) => {
        const byCommunication = rankByState[first.communicationState] - rankByState[second.communicationState];
        if (byCommunication !== 0) {
            return byCommunication;
        }
        return first.powerStationName.localeCompare(second.powerStationName, 'pt-BR');
    });
}

export function normalizePerformanceAnalysisSortFilters(
    filters: ADataGridFilter[]
): ADataGridFilter[] {
    const communicationSorted = filters.some(
        (filter) => filter.name === 'communicationState' && isActiveSort(filter.sort)
    );
    const anotherColumnSorted = filters.some(
        (filter) =>
            filter.name !== 'communicationState'
            && filter.name !== 'performanceRatio'
            && isActiveSort(filter.sort)
    );

    return filters.map((filter) => {
        if (communicationSorted && filter.name !== 'communicationState') {
            return {...filter, sort: 'none'};
        }

        if (filter.name === 'communicationState') {
            return {
                ...filter,
                sort: isActiveSort(filter.sort) ? filter.sort : 'none',
            };
        }

        if (filter.name === 'performanceRatio') {
            if (anotherColumnSorted) {
                return {...filter, sort: 'none'};
            }
            // SGD-3816: sem outra ordenação, o padrão da Visão Diária é PR crescente.
            return {
                ...filter,
                sort: isActiveSort(filter.sort) ? filter.sort : 'asc',
            };
        }

        return {...filter};
    });
}

// Ordenação aplicada ao grid de fora (pelo gráfico Energia Gerada da Visão Diária). SGD-3816.
export interface PerformanceAnalysisExternalSort {
    column: string;
    direction: 'asc' | 'desc';
    // Muda a cada escolha no gráfico, para reaplicar mesmo se o usuário tiver mexido no grid depois.
    requestId: number;
}

const CLEARED_SORT: ADataGridFilter['sort'] = 'none';

/**
 * 'A configuração no Grid deve ser limpa e substituída pela selecionada no gráfico' (SGD-3816, RN 3.e):
 * remove a ordenação de todas as colunas e aplica só a da coluna escolhida. Mantém os filtros de valor.
 */
export function applyExclusiveSort(
    filters: Array<ADataGridFilter>,
    column: string,
    direction: 'asc' | 'desc'
): Array<ADataGridFilter> {
    const result: Array<ADataGridFilter> = filters.map((filter) => ({
        ...filter,
        sort: filter.name === column ? direction : CLEARED_SORT,
    }));
    if (!result.some((filter) => filter.name === column)) {
        result.push({name: column, condition: 'contains', value: '', sort: direction});
    }
    return result;
}
